// Score normalisation.
//
// The hub stores `metric_primary` as an integer 0..100 for every app (`apps.score_max = 100`), so
// Big Dog must map its native run onto that scale here — not at the submission boundary.
//
// The stress term is the thematic core: without it the optimal strategy is "bark constantly and
// bulldoze", which is precisely the play the game is about discouraging. Do not drop it to
// simplify tuning.

import { clamp } from './vec2'
import type { Level, RunResult } from './types'

export interface ScoreBreakdown {
  /** 0..100, the hub's metric_primary. */
  primary: number
  /** Milliseconds, the hub's metric_secondary. */
  durationMs: number
  timePenalty: number
  barkPenalty: number
  stressPenalty: number
  bitePenalty: number
  escapePenalty: number
  crossPenalty: number
  incompletePenalty: number
}

/**
 * Time is charged RELATIVE to par, not in absolute seconds, and the difference is the whole reason
 * this was retuned.
 *
 * The first model charged 1 point per 2 seconds over par. Measured against real par values (13-28s)
 * that made the scale almost flat: a player taking SIX TIMES the bot's median still scored 61, and
 * one taking twice as long scored 86 against the bot's 91. Nearly every run landed in 85-95, which
 * is not a leaderboard, it is a rounding error.
 *
 * Absolute seconds also mean the same lateness costs differently on different layouts: 20s over par
 * is a rout on funnel (par 13) and a near miss on long-drive (par 28). Par-relative fixes both.
 *
 * Full penalty at 4x par. At par: 0; at 2x par a third of the cap; at 4x par and beyond, all of it.
 *
 * 4x rather than 3x is a deliberate hedge on an unknown: NO HUMAN HAS PLAYED A SCORED RUN YET. The
 * only pace data in existence is the bot's, and a bot that reads every sheep's velocity is not the
 * distribution being scored. Saturating at 3x put a first-timer straight on the floor, where a 3x
 * run and a 6x run score the same and the number stops saying anything. This keeps resolution
 * across the band a human is actually likely to occupy.
 *
 * Re-tune this against the first real runs — it is one constant and the tests pin the SHAPE
 * (monotonic, zero at par, saturating) rather than any particular value.
 */
export const TIME_PENALTY_CAP = 40

/** Multiple of par at which the time penalty saturates. */
export const TIME_PENALTY_FULL_AT = 4
export const BARK_PENALTY_CAP = 20
export const STRESS_PENALTY_CAP = 20

/**
 * Fault deductions — the trial half of the score (PLAN.md § Trial scoring, M10).
 *
 * These are things the BOT does not do, which is the point of them: it avoids sheep, so it does not
 * bite; it holds balance, so it does not cross; it does not lose sheep out of the pen. Par is the
 * bot's clock, so adding faults it never commits leaves par and the solvability gate untouched
 * while giving a human's worse play somewhere to lose points that a time penalty cannot express.
 *
 * "Rushed the stock" is deliberately NOT here. It is the same event the stress penalty already
 * charges, and scoring it twice would punish one mistake in two currencies; it is counted for the
 * scorecard so the stress number can be read back as incidents, and that is all.
 */
export const BITE_PENALTY = 4
export const BITE_PENALTY_CAP = 12
export const ESCAPE_PENALTY = 3
export const ESCAPE_PENALTY_CAP = 9
export const CROSS_PENALTY = 5
export const CROSS_PENALTY_CAP = 15

export function scoreRun(result: RunResult, level: Level): ScoreBreakdown {
  const seconds = result.elapsedMs / 1000

  // Fraction of par by which the run overshot: 0 at par, 1 at double par, 2 at triple.
  const over = level.parSeconds > 0 ? Math.max(0, seconds - level.parSeconds) / level.parSeconds : 0
  const timePenalty = clamp(
    (over / (TIME_PENALTY_FULL_AT - 1)) * TIME_PENALTY_CAP,
    0,
    TIME_PENALTY_CAP,
  )

  // 2 points per bark over the layout's par.
  const barkPenalty = clamp(Math.max(0, result.barks - level.parBarks) * 2, 0, BARK_PENALTY_CAP)

  // Mean flock stress integrated over the run, already normalised to [0,1] by the sim.
  const stressPenalty = clamp(result.meanStress * STRESS_PENALTY_CAP, 0, STRESS_PENALTY_CAP)

  // Partial completion. Proportional, and reported for transparency, but it is NOT what ranks an
  // unfinished run — see below.
  const missing = result.flockSize - result.penned
  const incompletePenalty =
    result.flockSize > 0 ? (missing / result.flockSize) * 100 : 0

  const f = result.faults
  const bitePenalty = clamp(f.bites * BITE_PENALTY, 0, BITE_PENALTY_CAP)
  const escapePenalty = clamp(f.penEscapes * ESCAPE_PENALTY, 0, ESCAPE_PENALTY_CAP)
  const crossPenalty = clamp(f.crosses * CROSS_PENALTY, 0, CROSS_PENALTY_CAP)

  const finished = Math.round(
    clamp(
      100 - timePenalty - barkPenalty - stressPenalty - bitePenalty - escapePenalty - crossPenalty,
      0,
      100,
    ),
  )

  /**
   * An unfinished run scores below EVERY finished one, ranked among its peers by how much of the
   * flock got in.
   *
   * The proportional penalty alone did not do this. With 12 sheep, leaving one out cost 8 points:
   * a near-perfect run that abandoned the last sheep scored 92, while a complete run that took 50x
   * par scored 20. The moment a quit button exists — M5½ adds a menu — the optimal play becomes
   * giving up on the one stubborn sheep, and that sheep is the entire game. Penning the flock is
   * the win condition, so not penning it cannot outscore penning it badly.
   *
   * The worst possible finished run is 100 - 40 - 20 - 20 = 20, so unfinished runs occupy 0..19.
   */
  const primary = result.won
    ? finished
    : Math.round(clamp((result.flockSize > 0 ? result.penned / result.flockSize : 0) * 19, 0, 19))

  return {
    primary,
    durationMs: result.elapsedMs,
    timePenalty,
    barkPenalty,
    stressPenalty,
    bitePenalty,
    escapePenalty,
    crossPenalty,
    incompletePenalty,
  }
}
